import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, Mail, Key, CheckCircle, AlertCircle, Info } from "lucide-react";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

// Updated password regex to match Supabase requirements
const passwordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
    newPassword: z
      .string()
      .min(8, "New password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
        "Password must contain at least 8 characters, including uppercase, lowercase, number, and special character."
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

// Google logo SVG component
const GoogleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    className="text-blue-500"
  >
    <path
      fill="#4285F4"
      d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09c1.97 3.92 6.02 6.62 10.71 6.62z"
    />
    <path
      fill="#FBBC05"
      d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29v-3.09h-3.98c-.82 1.62-1.29 3.44-1.29 5.38s.47 3.76 1.29 5.38l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42c-2.07-1.94-4.78-3.13-8.02-3.13-4.69 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"
    />
  </svg>
);

type EmailFormValues = z.infer<typeof emailSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function SecuritySettingsForm() {
  const { user, signOut, session } = useAuth();
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [emailUpdateSuccess, setEmailUpdateSuccess] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleUpdateEmail = async (data: EmailFormValues) => {
    if (!user) return;

    try {
      setIsUpdatingEmail(true);
      setEmailUpdateSuccess(false);
      
      const { error } = await supabase.auth.updateUser({
        email: data.email,
      });

      if (error) throw error;

      toast.success(
        "Email update initiated! Please check your inbox for verification."
      );
      emailForm.reset();
      setEmailUpdateSuccess(true);
    } catch (error: any) {
      toast.error(`Failed to update email: ${error.message}`);
      setEmailUpdateSuccess(false);
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async (data: PasswordFormValues) => {
    if (!user) return;

    try {
      setIsUpdatingPassword(true);
      setPasswordUpdateSuccess(false);

      // First verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email || "",
        password: data.currentPassword,
      });

      if (signInError) {
        toast.error("Current password is incorrect");
        setIsUpdatingPassword(false);
        return;
      }

      // If current password is verified, update to new password
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (error) throw error;

      toast.success("Password updated successfully!");
      passwordForm.reset();
      setPasswordUpdateSuccess(true);
    } catch (error: any) {
      toast.error(`Failed to update password: ${error.message}`);
      setPasswordUpdateSuccess(false);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Check if user is authenticated with Google
  const isGoogleAuthenticated =
    !!user?.app_metadata?.provider && user.app_metadata.provider === "google";

  if (!user) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center p-4">
            Please sign in to manage your security settings.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Change Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Mail className="h-5 w-5" /> Change Email Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="text-sm text-muted-foreground mb-1">
              Current Email Address
            </div>
            <div className="font-medium">{user?.email}</div>
          </div>

          {isGoogleAuthenticated ? (
            <div className="flex items-center gap-2 p-4 bg-muted rounded-md">
              <GoogleIcon />
              <div>
                <p className="font-medium">Google Account Linked</p>
                <p className="text-sm text-muted-foreground">
                  Your email is managed by your Google account. To change your
                  email address, please update it through Google.
                </p>
              </div>
            </div>
          ) : (
            <>
              {emailUpdateSuccess ? (
                <div className="mb-5 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 p-4 rounded-md flex items-start">
                  <CheckCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Email update initiated</h4>
                    <p className="text-sm mt-1">
                      Please check your inbox at <span className="font-medium">{emailForm.getValues().email}</span> to 
                      verify your new email address. The change will take effect after verification.
                    </p>
                    <Button 
                      variant="link"
                      className="px-0 text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 h-auto py-0 mt-2"
                      onClick={() => setEmailUpdateSuccess(false)}
                    >
                      Change another email
                    </Button>
                  </div>
                </div>
              ) : (
                <Form {...emailForm}>
                  <form
                    onSubmit={emailForm.handleSubmit(handleUpdateEmail)}
                    className="space-y-4"
                  >
                    <FormField
                      control={emailForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Email Address</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter new email address"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      variant="purple"
                      disabled={isUpdatingEmail}
                    >
                      {isUpdatingEmail ? "Updating..." : "Update Email"}
                    </Button>

                    <div className="text-sm text-muted-foreground mt-2">
                      Note: You will need to verify your new email address before
                      the change takes effect.
                    </div>
                  </form>
                </Form>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Password Change Section - Only show for non-Google authenticated users */}
      {!isGoogleAuthenticated && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Key className="h-5 w-5" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            {passwordUpdateSuccess ? (
              <div className="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 p-4 rounded-md flex items-start">
                <CheckCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium">Password updated successfully</h4>
                  <p className="text-sm mt-1">
                    Your password has been changed. You can use your new password the next time you sign in.
                  </p>
                  <Button 
                    variant="link"
                    className="px-0 text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 h-auto py-0 mt-2"
                    onClick={() => setPasswordUpdateSuccess(false)}
                  >
                    Change password again
                  </Button>
                </div>
              </div>
            ) : (
              <Form {...passwordForm}>
                <form
                  onSubmit={passwordForm.handleSubmit(handleUpdatePassword)}
                  className="space-y-4"
                >
                  <FormField
                    control={passwordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Enter current password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Enter new password"
                            {...field}
                          />
                        </FormControl>
                        <div className="flex items-start mt-1.5 text-xs text-muted-foreground">
                          <Info className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                          <span>
                            Password must be at least 8 characters and include uppercase, 
                            lowercase, number, and special character.
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Confirm new password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    variant="purple"
                    disabled={isUpdatingPassword}
                  >
                    {isUpdatingPassword ? "Updating..." : "Update Password"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
